import { useEffect, useState } from 'react'
import { Analytics } from '@vercel/analytics/react'
import Menu from './components/Menu'
import Game3D from './components/Game3D'
import BotBattleMenu from './components/BotBattleMenu'
import BotBattleArena from './components/BotBattleArena'
import FeedbackModal from './components/FeedbackModal'
import LandingScene from './components/game3d/LandingScene'
import { useGameStore } from './stores/gameStore'
import { track, initNoTrack, isNoTrack } from './utils/analytics'

initNoTrack()

type Screen = 'menu' | 'game' | 'bot-battle-menu' | 'bot-battle-arena'

export default function App() {
  const [screen, setScreen] = useState<Screen>('menu')
  const [showFeedback, setShowFeedback] = useState(false)
  const { resetGame, setBotBattle } = useGameStore()

  const handleStartGame = () => {
    const { aiDifficulty, marbleStyle } = useGameStore.getState()
    resetGame()
    useGameStore.setState({ aiDifficulty, marbleStyle, botBattle: { ...useGameStore.getState().botBattle, enabled: false } })
    setScreen('game')
  }

  const handleStartBotBattle = () => {
    // resetGame() reverts botBattle to defaults, so re-apply the menu selections.
    const { botBattle } = useGameStore.getState()
    resetGame()
    setBotBattle({ ...botBattle, enabled: true })
    track('bot_battle_started', {
      bot1: botBattle.bot1Difficulty,
      bot2: botBattle.bot2Difficulty,
    })
    setScreen('bot-battle-arena')
  }

  // The main menu and the bot-battle setup share ONE persistent landing scene. Its
  // <section> + <LandingScene> live here (not inside each screen) so switching between
  // the two only swaps the sidebar — the WebGL canvas and its live marble games stay
  // mounted instead of reloading/restarting.
  const onLanding = screen === 'menu' || screen === 'bot-battle-menu'

  // Drives the CSS in index.css: on the landing route the page scrolls and the static
  // #page-copy below the fold is visible; in a game it's a locked full-screen board.
  useEffect(() => {
    document.body.dataset.route = onLanding ? 'landing' : 'game'
  }, [onLanding])

  return (
    <>
      {onLanding && (
        <main className="landing-shell">
          {screen === 'menu' ? (
            <Menu
              onStartGame={handleStartGame}
              onBotBattle={() => setScreen('bot-battle-menu')}
              onFeedback={() => {
                track('feedback_opened', { context: 'menu' })
                setShowFeedback(true)
              }}
            />
          ) : (
            <BotBattleMenu
              onStartBattle={handleStartBotBattle}
              onBack={() => setScreen('menu')}
            />
          )}
          <section className="landing-stage" aria-hidden="true">
            <LandingScene />
          </section>
        </main>
      )}
      {screen === 'game' && <Game3D onBack={() => setScreen('menu')} />}
      {screen === 'bot-battle-arena' && <BotBattleArena onBack={() => setScreen('menu')} />}
      {showFeedback && (
        <FeedbackModal context="menu" onClose={() => setShowFeedback(false)} />
      )}
      {/* beforeSend returning null drops the pageview too, not just custom events. */}
      <Analytics beforeSend={(event) => (isNoTrack() ? null : event)} />
    </>
  )
}
